import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { notificationsAPI } from '../services/api';
import { useWebSocket } from '../contexts/WebSocketContext';
import {
  BellIcon,
  BellSlashIcon,
  PartyIcon,
  CameraIcon,
  StarIcon,
  ChecklistIcon,
  RefreshIcon,
  MessageIcon,
  HeartIcon,
  TrophyIcon,
  AlertTriangleIcon,
  BadgeIcon,
  FlagIcon,
  TargetIcon,
  CheckCircleIcon,
  CrownIcon,
  InfoIcon,
  CalendarIcon,
  GiftIcon,
  ThumbsUpIcon
} from '../icons';

function NotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isConnected, unreadCount, notifications, subscribe, setNotifications, setUnreadCount } = useWebSocket();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);

  // Notification type icons mapping
  const getNotificationIcon = (notification) => {
    const iconProps = { style: { width: '20px', height: '20px' } };
    const { type, message } = notification;
    if (type === 'points_awarded' && message === 'point_reasons.kudos') {
      return <ThumbsUpIcon {...iconProps} />;
    }
    const icons = {
      event_created: <PartyIcon {...iconProps} />,
      event_media_uploaded: <CameraIcon {...iconProps} />,
      event_ended: <CalendarIcon {...iconProps} />,
      reviews_open: <StarIcon {...iconProps} />,
      task_created: <ChecklistIcon {...iconProps} />,
      task_updated: <RefreshIcon {...iconProps} />,
      task_complaint_filed: <FlagIcon {...iconProps} />,
      review_comment: <MessageIcon {...iconProps} />,
      review_reaction: <HeartIcon {...iconProps} />,
      points_awarded: <TrophyIcon {...iconProps} />,
      badge_earned: <BadgeIcon {...iconProps} />,
      challenge_created: <TargetIcon {...iconProps} />,
      challenge_resolved: <CheckCircleIcon {...iconProps} />,
      challenge_won: <CrownIcon {...iconProps} />,
      admin_message: <InfoIcon {...iconProps} />,
      transaction_reported: <AlertTriangleIcon {...iconProps} />,
      birthday: <GiftIcon {...iconProps} />,
    };
    return icons[type] || <BellIcon {...iconProps} />;
  };

  // Format time ago
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) {
      return t('just_now');
    }

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return t('minutes_ago', { count: minutes });
    }

    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return t('hours_ago', { count: hours });
    }

    const days = Math.floor(hours / 24);
    if (days < 7) {
      return t('days_ago', { count: days });
    }

    const weeks = Math.floor(days / 7);
    if (weeks < 4) {
      return t('weeks_ago', { count: weeks });
    }

    const months = Math.floor(days / 30);
    return t('months_ago', { count: months });
  };

  // Load unread count (fallback when WebSocket not connected)
  const loadUnreadCount = useCallback(async () => {
    if (isConnected) {
      // WebSocket will provide unread count
      return;
    }

    try {
      const response = await notificationsAPI.getUnreadCount();
      setUnreadCount(response.data.unread_count || 0);
    } catch (err) {
      console.error('Error loading unread count:', err);
    }
  }, [isConnected, setUnreadCount]);

  // Load notifications when dropdown opens
  const loadNotifications = useCallback(async () => {
    if (loading) return;

    setLoading(true);
    setError(null);

    try {
      const response = await notificationsAPI.getNotifications({
        limit: 10,
        skip: 0
      });
      setNotifications(response.data || []);
    } catch (err) {
      console.error('Error loading notifications:', err);
      setError(t('error_loading_notifications'));
    } finally {
      setLoading(false);
    }
  }, [loading, t]);

  // Toggle dropdown
  const toggleDropdown = async () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);

    if (newIsOpen) {
      loadNotifications();
      // Auto-mark all notifications as read when opening the dropdown
      if (unreadCount > 0) {
        try {
          await notificationsAPI.markAllAsRead();
          setUnreadCount(0);
          setNotifications((prev) =>
            prev.map((n) => ({ ...n, is_read: true }))
          );
        } catch (err) {
          console.error('Error marking all as read:', err);
        }
      }
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  // Subscribe to WebSocket events for real-time updates
  useEffect(() => {
    const unsubscribe = subscribe((message) => {
      if (message.type === 'notification_created') {
        console.log('New notification received:', message.notification.title);
      }
    });

    return unsubscribe;
  }, [subscribe]);

  // Initial load with fallback polling
  useEffect(() => {
    loadUnreadCount();

    // If WebSocket disconnected, fallback to polling
    if (!isConnected) {
      const pollInterval = setInterval(loadUnreadCount, 30000);
      return () => clearInterval(pollInterval);
    }
  }, [isConnected, loadUnreadCount]);

  // Handle notification click with comprehensive navigation
  const handleNotificationClick = async (notification) => {
    try {
      // Close the dropdown
      setIsOpen(false);

      // Navigate based on notification type and data
      if (notification.link) {
        navigate(notification.link);
        return;
      }

      // Parse data if it's a string
      const data = notification.data
        ? (typeof notification.data === 'string' ? JSON.parse(notification.data) : notification.data)
        : {};

      // Route based on notification type
      switch (notification.type) {
        case 'event_created':
          // Navigate to event page
          if (notification.related_id) {
            navigate(`/events/${notification.related_id}`);
          } else if (data.event_id) {
            navigate(`/events/${data.event_id}`);
          }
          break;
        case 'event_media_uploaded':
          if (notification.related_id) {
            navigate(`/events/${notification.related_id}`);
          } else if (data.event_id) {
            navigate(`/events/${data.event_id}`);
          }
          break;
        case 'reviews_open':
          // Navigate to event page
          if (notification.related_id) {
            navigate(`/events/${notification.related_id}`);
          } else if (data.event_id) {
            navigate(`/events/${data.event_id}`);
          }
          break;
        case 'task_created':
          // Navigate to event page
          if (notification.related_id) {
            navigate(`/events/${notification.related_id}`);
          } else if (data.event_id) {
            navigate(`/events/${data.event_id}`);
          }
          break;
        case 'task_updated':
          // Navigate to event page
          if (notification.related_id) {
            navigate(`/events/${notification.related_id}`);
          } else if (data.event_id) {
            navigate(`/events/${data.event_id}`);
          }
          break;
        case 'task_completion_request':
          // Navigate to event page
          if (notification.related_id) {
            navigate(`/events/${notification.related_id}`);
          } else if (data.event_id) {
            navigate(`/events/${data.event_id}`);
          }
          break;
        case 'task_completion_confirmed':
          // Navigate to event page
          if (notification.related_id) {
            navigate(`/events/${notification.related_id}`);
          } else if (data.event_id) {
            navigate(`/events/${data.event_id}`);
          }
          break;
        case 'task_completion_rejected':
          // Navigate to event page
          if (notification.related_id) {
            navigate(`/events/${notification.related_id}`);
          } else if (data.event_id) {
            navigate(`/events/${data.event_id}`);
          }
          break;
        case 'review_comment':
        case 'review_reaction':
          // Navigate to location page (reviews are associated with locations)
          if (data.location_id) {
            navigate(`/locations/${data.location_id}`);
          }
          break;
        case 'badge_earned':
          // Navigate to profile page to view badges
          navigate('/profile');
          break;
        case 'points_awarded':
          // Navigate to profile page to view points
          navigate('/profile');
          break;
        case 'challenge_created':
          // Navigate to challenges page
          if (notification.related_id) {
            navigate('/challenges');
          }
          break;
        case 'challenge_resolved':
          // Navigate to challenges page
          if (notification.related_id) {
            navigate('/challenges');
          }
          break;
        case 'challenge_won':
          // Navigate to challenges page
          if (notification.related_id) {
            navigate('/challenges');
          }
          break;

        default:
          // For unknown types, try to extract event_id or location_id from data
          if (data.event_id) {
            navigate(`/events/${data.event_id}`);
          } else if (data.location_id) {
            navigate(`/locations/${data.location_id}`);
          }
          break;
      }
    } catch (err) {
      console.error('Error handling notification click:', err);
      setIsOpen(false);
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async (e) => {
    e.stopPropagation();

    try {
      await notificationsAPI.markAllAsRead();
      setUnreadCount(0);
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, is_read: true }))
      );
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  // View all notifications
  const handleViewAll = () => {
    navigate('/notifications');
    setIsOpen(false);
  };

  return (
    <div className="notification-bell-container" ref={dropdownRef}>
      <button
        className="notification-bell"
        onClick={toggleDropdown}
        aria-label={t('notifications.notifications')}
        aria-expanded={isOpen}
      >
        <BellIcon className="bell-icon" width="24" height="24" />
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-dropdown-header">
            <h3>{t('notifications.notifications')}</h3>
            {notifications.some(n => !n.is_read) && (
              <button
                className="mark-all-read-btn"
                onClick={handleMarkAllAsRead}
              >
                {t('mark_all_read')}
              </button>
            )}
          </div>

          <div className="notification-dropdown-body">
            {loading && notifications.length === 0 ? (
              <div className="notification-loading">
                <div className="notification-spinner"></div>
                <p>{t('loading')}</p>
              </div>
            ) : error ? (
              <div className="notification-error">
                <AlertTriangleIcon className="error-icon" style={{ width: '32px', height: '32px' }} />
                <p>{error}</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="notification-empty">
                <BellSlashIcon className="empty-icon" style={{ width: '32px', height: '32px' }} />
                <p>{t('no_notifications')}</p>
              </div>
            ) : (
              <div className="notification-list">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`notification-item ${notification.is_read ? 'read' : 'unread'}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="notification-icon">
                      {getNotificationIcon(notification)}
                    </div>
                    <div className="notification-content">
                      <div className="notification-title">
                        {t(notification.title, notification.data || {})}
                      </div>
                      <div className="notification-message">
                        {t(notification.message, notification.data || {})}
                      </div>
                    </div>
                    <div className="notification-meta">
                      <div className="notification-time">
                        {formatTimeAgo(notification.created_at)}
                      </div>
                      {!notification.is_read && (
                        <div className="notification-unread-dot"></div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {notifications.length > 0 && (
            <div className="notification-dropdown-footer">
              <button
                className="view-all-btn"
                onClick={handleViewAll}
              >
                {t('view_all_notifications')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
